// Logistics & policies — Stream 3 PR-2.
//
// 4-cell read-only grid that summarises pickup/delivery/setup
// availability, lead time, and cancellation policy. Renters scan
// this before opening the booking / quote flow; the actual address
// + fulfillment selection still lives in the right-rail OrderPanel
// / QuoteRequestPanel.
//
// Stream 3 follow-up (2026-05-15): per-listing cancellation +
// damage policy fields were retired (the POLICIES wizard step is
// gone). The platform now applies a universal policy. The legacy
// publicData.cancellation_policy / damage_policy fields are still
// read here so listings that saved a per-listing override before
// the policy was universalized continue to render their saved
// choice; everything else falls back to UNIVERSAL_POLICY constants.

import React from 'react';
import classNames from 'classnames';

import { UNIVERSAL_CANCELLATION_POLICY } from '../../../data/cancellation-policies';

import css from './SectionLogisticsAndPolicies.module.css';
const UNIVERSAL_DAMAGE_POLICY =
  'Renters cover the replacement cost of any items returned damaged or missing. Hosts notify renters within 48 hours of pickup with a description and an invoice. Merrily mediates any disputes.';

const LEAD_TIME_LABELS = {
  '24h': '24 hours minimum',
  '48h': '48 hours minimum',
  '1w': '1 week minimum',
  '2w': '2 weeks minimum',
  '1m': '1 month minimum',
};

const Cell = ({ label, value }) => (
  <div className={css.cell}>
    <div className={css.cellLabel}>{label}</div>
    <div className={css.cellValue}>{value}</div>
  </div>
);

// Pickup/delivery summary string. Combines the three booleans into
// one human-readable line for the Delivery cell.
//   - "Pickup only" — pickup yes, delivery no
//   - "Delivery only — within X mi" — delivery yes, pickup no
//   - "Pickup or delivery within X mi" — both yes
//   - "Not specified" — neither (shouldn't happen post-wizard)
const formatDeliverySummary = (fulfillment, deliveryRadius) => {
  const hasPickup = fulfillment.includes('pickup');
  const hasDelivery = fulfillment.includes('delivery');
  const radius = deliveryRadius ? `${deliveryRadius} mi` : null;
  if (hasPickup && hasDelivery) {
    return radius ? `Pickup or delivery within ${radius}` : 'Pickup or delivery';
  }
  if (hasDelivery) {
    return radius ? `Delivery only, within ${radius}` : 'Delivery only';
  }
  if (hasPickup) return 'Pickup only';
  return 'Not specified';
};

// Setup summary. The setup line-item is optional add-on territory;
// keep this read-only summary terse.
const formatSetupSummary = (fulfillment, setupFee) => {
  const hasSetup = fulfillment.includes('setup');
  if (!hasSetup) return 'Not offered';
  if (setupFee) return `Available from $${setupFee}`;
  return 'Available as add-on';
};

const SectionLogisticsAndPolicies = props => {
  const { className, listing } = props;
  const publicData = listing?.attributes?.publicData || {};

  const fulfillment = Array.isArray(publicData.fulfillment)
    ? publicData.fulfillment
    : ['pickup'];
  const deliveryRadius = publicData.delivery_radius;
  const setupFee = publicData.setup_fee;

  const leadTimeKey = publicData.scheduling?.leadTime;
  const leadTimeValue = LEAD_TIME_LABELS[leadTimeKey] || 'Contact host';

  // Cancellation policy: universal for every listing (owner decision
  // 2026-07-02 — see docs/design/cancellation-policy-unification.md).
  // Legacy per-listing publicData.cancellation_policy overrides are no
  // longer honored: one policy, told the same way everywhere.
  const policy = UNIVERSAL_CANCELLATION_POLICY;
  const cancellationValue = policy.label;

  // Damage policy: per-listing override (legacy) takes precedence,
  // otherwise show the universal text.
  const damageText = publicData.damage_policy || UNIVERSAL_DAMAGE_POLICY;

  return (
    <section className={classNames(css.root, className)}>
      <h2 className={css.title}>Logistics &amp; policies</h2>
      <div className={css.grid}>
        <Cell
          label="Delivery"
          value={formatDeliverySummary(fulfillment, deliveryRadius)}
        />
        <Cell
          label="Setup"
          value={formatSetupSummary(fulfillment, setupFee)}
        />
        <Cell label="Lead time" value={leadTimeValue} />
        <Cell label="Cancellation" value={cancellationValue} />
      </div>
      {policy && (
        <p className={css.policyHelper}>{policy.summary}</p>
      )}
      <p className={css.damageHelper}>
        <strong>Damage policy:</strong> {damageText}
      </p>
    </section>
  );
};

// Exported for tests.
export {
  formatDeliverySummary,
  formatSetupSummary,
  LEAD_TIME_LABELS,
  UNIVERSAL_DAMAGE_POLICY,
};

export default SectionLogisticsAndPolicies;
